import {
  Activity, GitPullRequest, CircleDot, CheckCircle2, Gauge, Clock, TrendingUp, Bot, RefreshCw, ShieldAlert, Sparkles, ExternalLink,
} from 'lucide-react';
import type { PrSummary } from '../lib/data';
import { useReliabilityMetrics, useAgentStatus } from '../lib/data';
import { Skeleton, SkeletonChart } from './Skeleton';
import { Card, CardHeader, CardContent } from './ui/card';
import { PageHeader, ErrorState } from './ui/page';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { cn } from '../lib/cn';

/**
 * Operational overview dashboard — the landing page. Charts the *volume, status
 * mix and throughput* of the review pipeline. Quality depth lives in Reliability;
 * the per-PR board lives in Tasks (KanbanBoard). All data comes from existing
 * hooks (usePRs prop + useReliabilityMetrics + useAgentStatus) — no new endpoints.
 *
 * Synced with zworks-router-v2 primitives:
 *   - PageHeader (eyebrow + title + Export/Refresh)
 *   - Card / CardHeader / CardContent for section surfaces
 *   - Badge + tone-class helpers for PR status pills
 *   - Button + ErrorState for the retry flow
 * Donut + score chart + onboarding banner stay bespoke (dataviz / brand).
 */

// ponytail: categorical palette is the app's existing one (not newly introduced),
// so the dataviz palette validator isn't the gate here. If we ever rebrand, run
// dataviz/scripts/validate_palette.js against these four hues + both surfaces.
const STATUS_SLICES = [
  { key: 'open', label: 'Open', color: '#2383E2' },
  { key: 'reviewed', label: 'Reviewed', color: '#9065B0' },
  { key: 'merged', label: 'Merged', color: '#2E7D32' },
  { key: 'closed', label: 'Closed', color: '#91918E' },
] as const;

interface DashboardOverviewProps {
  prs: PrSummary[];
  onSelectPr: (id: string) => void;
  loading: boolean;
}

export function DashboardOverview({ prs, onSelectPr, loading }: DashboardOverviewProps) {
  const { metrics, loading: loadingRel, error: relError, retry: retryRel } = useReliabilityMetrics();
  const { status: agent, loading: loadingAgent, error: agentError, retry: retryAgent } = useAgentStatus();

  // --- PR status mix (operational headline) ---
  const counts = STATUS_SLICES.map((s) => ({
    ...s,
    value: prs.filter((p) => p.status === s.key).length,
  }));
  const total = prs.length;
  const mergedOrClosed = counts.find((c) => c.key === 'merged')!.value + counts.find((c) => c.key === 'closed')!.value;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Overview"
        title="Review pipeline at a glance"
        description="Volume, status mix and throughput of the autonomous PR review pipeline. Quality depth lives in Reliability; the per-PR board lives in Tasks."
        onRefresh={() => window.location.reload()}
      />

      <AppSetupBanner />

      {/* KPI row — uses the same Card primitive as router's metric tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={GitPullRequest} label="Total PRs" value={loading ? null : total} sub="across all repos" tone="var(--accent)" />
        <MetricCard icon={CircleDot} label="Open" value={loading ? null : counts.find((c) => c.key === 'open')!.value} sub="awaiting review" tone="#2383E2" />
        <MetricCard icon={CheckCircle2} label="Reviewed" value={loading ? null : counts.find((c) => c.key === 'reviewed')!.value} sub="review complete" tone="#9065B0" />
        <MetricCard icon={Gauge} label="Merged / Closed" value={loading ? null : mergedOrClosed} sub="finished" tone="#2E7D32" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Status mix donut */}
        <Card>
          <CardHeader>
            <Activity className="size-4 text-primary" />
            <h3 className="text-sm font-semibold tracking-tight">Pipeline status</h3>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-4"><Skeleton width={160} height={160} rounded={80} /></div>
            ) : total === 0 ? (
              <Empty text="No pull requests yet." />
            ) : (
              <StatusDonut slices={counts} total={total} />
            )}
          </CardContent>
        </Card>

        {/* Score trend (quality headline; depth in Reliability) */}
        <Card>
          <CardHeader>
            <TrendingUp className="size-4 text-primary" />
            <h3 className="text-sm font-semibold tracking-tight">Review score trend</h3>
          </CardHeader>
          <CardContent>
            {loadingRel ? (
              <SkeletonChart height={128} />
            ) : relError ? (
              <ErrorState message={relError} retry={retryRel} />
            ) : !metrics || metrics.scoreTrend.length === 0 ? (
              <Empty text="No reviews yet." />
            ) : (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl font-bold text-foreground">{metrics.avgScore}</span>
                  <span className="text-[10px] text-muted-foreground">avg score / 100</span>
                </div>
                <ScoreTrend points={metrics.scoreTrend} />
                <div className="mt-2 text-[10px] font-mono text-muted-foreground">
                  last {metrics.scoreTrend.length} review{metrics.scoreTrend.length === 1 ? '' : 's'}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Queue & in-flight (throughput) */}
        <Card>
          <CardHeader>
            <Bot className="size-4 text-primary" />
            <h3 className="text-sm font-semibold tracking-tight">Review queue</h3>
          </CardHeader>
          <CardContent>
            {loadingAgent ? (
              <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} width="80%" height={28} rounded={6} />)}</div>
            ) : agentError ? (
              <ErrorState message={agentError} retry={retryAgent} />
            ) : !agent ? (
              <Empty text="Agent status unavailable." />
            ) : (
              <QueuePanel pending={agent.pending} running={agent.running} inFlight={agent.inFlight} />
            )}
          </CardContent>
        </Card>

        {/* Recent reviews (activity) */}
        <Card>
          <CardHeader>
            <Clock className="size-4 text-primary" />
            <h3 className="text-sm font-semibold tracking-tight">Recent reviews</h3>
          </CardHeader>
          <CardContent>
            {loadingAgent ? (
              <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} width="90%" height={24} rounded={5} />)}</div>
            ) : agentError ? (
              <ErrorState message={agentError} retry={retryAgent} />
            ) : !agent || agent.recentReviews.length === 0 ? (
              <Empty text="No recent reviews." />
            ) : (
              <RecentReviews items={agent.recentReviews} onSelectPr={onSelectPr} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ---------------- primitives (match Reliability.tsx idioms) ---------------- */

function MetricCard({ icon: Icon, label, value, sub, tone }: {
  icon: typeof Activity; label: string; value: number | null; sub?: string; tone: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="p-2.5 rounded-md bg-muted" style={{ color: tone }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
          {value === null ? (
            <Skeleton width={48} height={24} rounded={4} />
          ) : (
            <div className="text-xl font-bold text-foreground">{value}</div>
          )}
          {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="py-8 text-center text-xs text-muted-foreground">{text}</div>;
}

/** Categorical donut: fixed-order slices + legend (identity never color-alone).
 *  Each slice carries a native <title> for a hover tooltip. */
function StatusDonut({ slices, total }: {
  slices: { key: string; label: string; value: number; color: string }[];
  total: number;
}) {
  const visible = slices.filter((s) => s.value > 0);
  const center = 80;
  const radius = 55;
  const strokeWidth = 14;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;

  return (
    <div className="flex items-center gap-5">
      <div className="relative flex-shrink-0">
        <svg width="160" height="160" viewBox="0 0 160 160">
          {visible.length === 0 && (
            <circle cx={center} cy={center} r={radius} fill="transparent" stroke="var(--ink-faint)" strokeWidth={strokeWidth} />
          )}
          {visible.map((s) => {
            const pct = total > 0 ? (s.value / total) * 100 : 0;
            const dash = `${(pct / 100) * circumference} ${circumference}`;
            const offset = -((cumulative / 100) * circumference);
            cumulative += pct;
            return (
              <circle
                key={s.key}
                cx={center}
                cy={center}
                r={radius}
                fill="transparent"
                stroke={s.color}
                strokeWidth={strokeWidth}
                strokeDasharray={dash}
                strokeDashoffset={offset}
                transform={`rotate(-90 ${center} ${center})`}
              >
                <title>{`${s.label}: ${s.value} (${Math.round(pct)}%)`}</title>
              </circle>
            );
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-extrabold text-foreground">{total}</span>
          <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">PRs</span>
        </div>
      </div>

      <div className="space-y-1.5 min-w-0">
        {slices.map((s) => {
          const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
          return (
            <div key={s.key} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
              <span className="truncate text-foreground">{s.label}</span>
              <span className="ml-auto font-mono text-muted-foreground">{s.value}</span>
              <span className="font-mono w-9 text-right text-muted-foreground">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Inline-SVG line chart of review scores (0–100). No chart lib. */
function ScoreTrend({ points }: { points: { ts: string; score: number }[] }) {
  const W = 520;
  const H = 120;
  const PAD = 8;
  const xs = points.length === 1 ? [W / 2] : points.map((_, i) => PAD + (i * (W - 2 * PAD)) / Math.max(1, points.length - 1));
  const ys = points.map((p) => H - PAD - (p.score / 100) * (H - 2 * PAD));
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${d} L${xs[xs.length - 1].toFixed(1)},${H - PAD} L${xs[0].toFixed(1)},${H - PAD} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" preserveAspectRatio="none">
      <defs>
        <linearGradient id="dashScoreFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((g) => (
        <line key={g} x1={PAD} x2={W - PAD} y1={PAD + g * (H - 2 * PAD)} y2={PAD + g * (H - 2 * PAD)} stroke="var(--border)" strokeWidth="1" />
      ))}
      <path d={area} fill="url(#dashScoreFill)" />
      <path d={d} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {xs.map((x, i) => <circle key={i} cx={x} cy={ys[i]} r="2" fill="var(--accent)" />)}
    </svg>
  );
}

function QueuePanel({ pending, running, inFlight }: {
  pending: number; running: number;
  inFlight: { taskId: string; prId: string; repo: string; prNumber: number; status: string; ageMs: number }[];
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md border border-border p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pending</div>
          <div className="text-xl font-bold text-foreground">{pending}</div>
        </div>
        <div className="rounded-md border border-border p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Running</div>
          <div className="text-xl font-bold text-primary">{running}</div>
        </div>
      </div>
      {inFlight.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">In flight</div>
          {inFlight.slice(0, 4).map((t) => (
            <div key={t.taskId} className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <Badge className={cn(
                'font-mono',
                t.status === 'running' && 'bg-primary/10 text-primary border-primary/20',
                t.status === 'pending' && 'bg-warning/10 text-warning border-warning/20',
                t.status === 'completed' && 'bg-success/10 text-success border-success/20',
                t.status === 'failed' && 'bg-destructive/10 text-destructive border-destructive/20',
              )}>
                {t.status}
              </Badge>
              <span className="truncate">{t.repo}#{t.prNumber}</span>
              <span className="ml-auto text-foreground">{fmtAge(t.ageMs)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RecentReviews({ items, onSelectPr }: {
  items: { prId: string; score: number; conclusion: string; timestamp: string; title: string | null; repo: string | null; prNumber: number | null }[];
  onSelectPr: (id: string) => void;
}) {
  const conclusionClass = (c: string) =>
    c === 'approved'
      ? 'bg-success/10 text-success border-success/20'
      : c === 'changes_requested'
      ? 'bg-destructive/10 text-destructive border-destructive/20'
      : 'bg-warning/10 text-warning border-warning/20';
  return (
    <div className="space-y-1">
      {items.slice(0, 6).map((r) => (
        <button
          key={r.prId + r.timestamp}
          onClick={() => onSelectPr(r.prId)}
          className="w-full flex items-center gap-2 text-left rounded-md px-2 py-1.5 transition hover:bg-muted"
        >
          <Badge className={cn('capitalize', conclusionClass(r.conclusion))}>
            {r.conclusion.replace(/_/g, ' ')}
          </Badge>
          <span className="truncate text-xs text-foreground">{r.title || (r.repo && r.prNumber ? `${r.repo}#${r.prNumber}` : r.prId)}</span>
          <span className="ml-auto flex-shrink-0 font-mono text-[11px] text-muted-foreground">{r.score}</span>
        </button>
      ))}
    </div>
  );
}

function fmtAge(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  return `${Math.round(ms / 3_600_000)}h`;
}

/** Quick GitHub App setup — the onboarding banner. Lives on the landing dashboard
 *  so admins see it first; was previously on the Pull Requests page. */
function AppSetupBanner() {
  return (
    <section
      className="rounded-xl p-5 md:p-6 shadow-sm relative overflow-hidden"
      style={{ background: 'linear-gradient(to right, color-mix(in srgb, var(--accent) 8%, var(--bg)), var(--bg))', border: '1px solid var(--border)' }}
      id="admin-install-guide"
    >
      <div className="flex flex-col lg:flex-row gap-6 justify-between items-start relative z-10">
        <div className="space-y-3 max-w-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-white text-[10px] font-mono font-bold tracking-widest px-2.5 py-1 rounded uppercase shadow-sm flex items-center gap-1" style={{ background: 'var(--accent)' }}>
              <ShieldAlert className="w-3 h-3" /> Admin Only
            </span>
            <Badge className="font-mono font-bold tracking-wider bg-primary/10 text-primary border-primary/20">
              🤖 Nixor Nim Bot Integration
            </Badge>
          </div>

          <div className="space-y-1.5">
            <h2 className="text-lg md:text-xl font-bold tracking-tight flex items-center gap-2 text-foreground">
              <Bot className="w-5 h-5 text-primary" />
              Quick GitHub App Setup
            </h2>
            <p className="text-xs leading-relaxed max-w-xl text-muted-foreground">
              To activate autonomous pull request feedback and self-correcting review loops in your GitHub repositories, install the pre-configured GitHub App.
            </p>
          </div>

          {/* Quick Installation Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1 text-xs">
            {[
              { t: 'Step 1. Link App', d: 'Click the link button to configure/install on your personal account or GitHub Org.' },
              { t: 'Step 2. Grant Access', d: 'Choose "All Repositories" or select specific review targets for automatic triggers.' },
              { t: 'Step 3. Rules Sync', d: 'Ensure Webhook Secrets are matching with your CLI rules console to start audit loops.' },
            ].map((s) => (
              <div key={s.t} className="p-3 rounded-lg border border-border bg-card">
                <div className="font-mono font-bold text-[11px] text-primary">{s.t}</div>
                <p className="text-[11px] leading-snug mt-0.5 text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full lg:w-auto shrink-0">
          <Button
            variant="default"
            className="text-white text-xs font-bold px-6 py-3 shadow-md"
            onClick={() => window.open('https://github.com/settings/apps/nixor-nim-bot', '_blank', 'noreferrer,noopener')}
            id="install-bot-link"
          >
            <Sparkles className="size-4 opacity-80" />
            <span>Install Nixor Nim Bot</span>
            <ExternalLink className="size-3.5" />
          </Button>
          <div className="text-center lg:text-left font-mono text-[10px] rounded p-2.5 border border-border bg-card text-muted-foreground">
            App Name: <span className="font-bold text-foreground">nixor-nim-bot</span>
          </div>
        </div>
      </div>
    </section>
  );
}
